import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Settings, Volume2, VolumeX, Mail, Info, Timer, Bell, BellOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { TIME_OPTIONS, getTimeLimit, setTimeLimit as saveTimeLimit } from "../lib/timerSettings";
import { getSoundsMuted, setSoundsMuted } from "../lib/sounds";

const KEY = "cs.audio.muted";
const HIDE_ON = ["/", "/splash"];
const CONTACT = "hgmenmusic@gmail.com";
const CREDITS = "HGM Productions";

// User preference persistence — not sensitive; localStorage is intentional.
const loadMuted = () => {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.debug("SettingsMenu: localStorage unavailable", err);
    return false;
  }
};

export const SettingsMenu = () => {
  const loc = useLocation();
  const [muted, setMuted] = useState(() => loadMuted());
  const [sfxMuted, setSfxMuted] = useState(() => getSoundsMuted());
  const [timeLimit, setTimeLimitState] = useState(() => getTimeLimit());
  const [open, setOpen] = useState(false);

  // Keep local state synced when AudioLoop or other tabs change it.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEY) setMuted(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (HIDE_ON.includes(loc.pathname)) return null;

  const toggleMuted = () => {
    const next = !muted;
    setMuted(next);
    try { localStorage.setItem(KEY, next ? "1" : "0"); } catch {}
    // Broadcast so AudioLoop can respond immediately in the same tab.
    window.dispatchEvent(new CustomEvent("cs:audio-toggle", { detail: { muted: next } }));
  };

  const toggleSfx = () => {
    const next = !sfxMuted;
    setSfxMuted(next);
    setSoundsMuted(next);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="settings-open-btn"
          aria-label="Open settings"
          className="fixed top-3 left-3 sm:top-4 sm:left-4 z-50 w-10 h-10 rounded-full backdrop-blur-md bg-black/60 border border-white/15 text-[#FDFBF7] flex items-center justify-center hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors"
        >
          <Settings size={16} />
        </button>
      </DialogTrigger>

      <DialogContent
        data-testid="settings-dialog"
        className="max-w-sm rounded-2xl border border-white/15 bg-[#16141D] text-[#FDFBF7] p-0 overflow-hidden"
      >
        {/* Header */}
        <div className="relative px-5 pt-5 pb-4 border-b border-white/10">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#00E5FF] font-bold">Menu</div>
          <DialogTitle asChild>
            <h2 className="font-display text-3xl leading-none tracking-tight mt-1">Settings</h2>
          </DialogTitle>
          <DialogDescription asChild>
            <p className="text-xs text-[#FDFBF7]/60 mt-1">Adjust audio, view credits, and reach the developer.</p>
          </DialogDescription>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Music toggle */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FF107A]/15 border border-[#FF107A]/40 flex items-center justify-center text-[#FF107A]">
                {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </div>
              <div>
                <div className="text-sm font-semibold">Background music</div>
                <div className="text-[11px] text-[#FDFBF7]/50">Loop plays during the game</div>
              </div>
            </div>
            <button
              data-testid="settings-music-toggle"
              role="switch"
              aria-checked={!muted}
              onClick={toggleMuted}
              className={`relative w-14 h-8 rounded-full border transition-colors ${
                !muted ? "bg-[#FF107A]/80 border-[#FF107A]" : "bg-white/10 border-white/20"
              }`}
            >
              <span
                className={`absolute top-1 w-6 h-6 rounded-full bg-[#FDFBF7] shadow transition-transform ${
                  !muted ? "translate-x-7" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Game sounds toggle */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#A855F7]/15 border border-[#A855F7]/40 flex items-center justify-center text-[#A855F7]">
                {sfxMuted ? <BellOff size={18} /> : <Bell size={18} />}
              </div>
              <div>
                <div className="text-sm font-semibold">Game sounds</div>
                <div className="text-[11px] text-[#FDFBF7]/50">Clicks, chimes, and timer beeps</div>
              </div>
            </div>
            <button
              data-testid="settings-sfx-toggle"
              role="switch"
              aria-checked={!sfxMuted}
              onClick={toggleSfx}
              className={`relative w-14 h-8 rounded-full border transition-colors ${
                !sfxMuted ? "bg-[#A855F7]/80 border-[#A855F7]" : "bg-white/10 border-white/20"
              }`}
            >
              <span
                className={`absolute top-1 w-6 h-6 rounded-full bg-[#FDFBF7] shadow transition-transform ${
                  !sfxMuted ? "translate-x-7" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Round time limit */}
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#00E5FF]/15 border border-[#00E5FF]/40 flex items-center justify-center text-[#00E5FF]">
                <Timer size={18} />
              </div>
              <div>
                <div className="text-sm font-semibold">Round time limit</div>
                <div className="text-[11px] text-[#FDFBF7]/50">Countdown per round · beeps for the last 10s</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2" data-testid="settings-time-limit-group">
              {TIME_OPTIONS.map((o) => {
                const sel = timeLimit === o.seconds;
                return (
                  <button
                    key={o.seconds}
                    data-testid={`settings-time-limit-${o.seconds}`}
                    onClick={() => {
                      setTimeLimitState(o.seconds);
                      saveTimeLimit(o.seconds);
                    }}
                    className={`rounded-xl border-2 py-2 px-1 text-center transition-all ${
                      sel
                        ? "border-[#FF107A] bg-[#FF107A]/15 text-[#FDFBF7]"
                        : "border-white/10 bg-white/5 text-[#FDFBF7]/70 hover:border-[#00E5FF]"
                    }`}
                  >
                    <div className="font-display text-lg leading-none tracking-tight">{o.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Credits */}
          <div className="rounded-xl border border-white/10 bg-black/30 p-4">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#A855F7] font-bold">
              <Info size={12} /> Credits
            </div>
            <div
              data-testid="settings-credits"
              className="font-display text-2xl tracking-tight mt-2 leading-none"
            >
              {CREDITS}
            </div>
            <p className="text-[11px] text-[#FDFBF7]/60 mt-1.5">
              Game design, sound, and creative direction.
            </p>
          </div>

          {/* Contact developer */}
          <a
            data-testid="settings-contact-link"
            href={`mailto:${CONTACT}?subject=Celebrity%20Smash%20feedback`}
            className="flex items-center gap-3 rounded-xl border border-[#00E5FF]/30 bg-[#00E5FF]/5 p-4 hover:bg-[#00E5FF]/15 transition-colors group"
          >
            <div className="w-10 h-10 rounded-xl bg-[#00E5FF]/20 border border-[#00E5FF]/50 flex items-center justify-center text-[#00E5FF]">
              <Mail size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#FDFBF7] group-hover:text-[#00E5FF]">
                Contact developer
              </div>
              <div className="text-[11px] text-[#FDFBF7]/70 truncate">{CONTACT}</div>
            </div>
          </a>

          <p className="text-center text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7]/40 pt-1">
            Celebrity Smash · Beta
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
